/**
 * Click-to-place preview for Quick Chat / Voice / Comment — constant 40×40
 * screen px, centered on the cursor. Chat and voice mirror ChatSessionNode's
 * rust bubble; comment mirrors the pin it is about to drop, and also outlines
 * the node the drop would anchor to.
 */

import { cn } from "@/utils";
import { useStore, useViewport, ViewportPortal } from "@xyflow/react";
import { MessageSquare, Mic } from "lucide-react";
import { useMemo } from "react";
import { CanvasCommentSelfPin } from "../comments/CanvasCommentSelfPin";
import { absoluteNodePosition } from "../comments/threadPlacement";
import {
  type DeferredQuickComposeGhost,
  QUICK_COMPOSE_GHOST_ICON_SIZE,
} from "../hooks/useDeferredQuickChatPlacement";
import { nodeBoundsById } from "../utils/nodeBounds";

export function DeferredQuickComposeGhostPreview({
  ghost,
}: {
  ghost: DeferredQuickComposeGhost;
}) {
  const { zoom } = useViewport();
  const snapBounds = useSnapNodeBounds(ghost.snapNodeId);
  const inverseScale = 1 / zoom;
  const halfIconFlow = (QUICK_COMPOSE_GHOST_ICON_SIZE / 2) * inverseScale;
  const Icon = ghost.mode === "quick-voice" ? Mic : MessageSquare;

  return (
    <ViewportPortal>
      {snapBounds && (
        <div
          className="pointer-events-none absolute rounded-md ring-2 ring-canvas-selection/70"
          style={{
            left: snapBounds.x,
            top: snapBounds.y,
            width: snapBounds.width,
            height: snapBounds.height,
          }}
        />
      )}
      <div
        className="absolute pointer-events-none"
        style={{
          left: ghost.center.x - halfIconFlow,
          top: ghost.center.y - halfIconFlow,
          transform: `scale(${inverseScale})`,
          transformOrigin: "top left",
        }}
      >
        {/* A comment is people, not the agent: the ghost is the pin itself,
            translucent, wearing your face — not the rust compose bubble. */}
        {ghost.mode === "comment" ? (
          <CanvasCommentSelfPin className="opacity-70" />
        ) : (
          <div
            className={cn(
              "relative flex size-10 items-center justify-center rounded-full shadow-elev-4",
              "bg-rust/70 ring-2 ring-rust/50",
            )}
          >
            <Icon className="size-5 text-paper" strokeWidth={2} />
          </div>
        )}
      </div>
    </ViewportPortal>
  );
}

/** Bounds of the node the comment ghost would anchor to, in flow space. */
function useSnapNodeBounds(nodeId: string | undefined) {
  const nodes = useStore((s) => s.nodes);
  return useMemo(() => {
    if (!nodeId) return null;
    const byId = nodeBoundsById(nodes);
    const node = byId.get(nodeId);
    if (!node) return null;
    const absolute = absoluteNodePosition(nodeId, byId);
    if (!absolute) return null;
    return { ...absolute, width: node.width, height: node.height };
  }, [nodeId, nodes]);
}
